import type { CardLook, CardUse } from './profile-card';

/**
 * The card maker (module API 4.10; the platform's card editor and the example module org.vp.cards).
 * A card as the editor keeps it: a design size and elements placed on it (px of the design). The
 * generator turns it into the HTML of a profile card (module API 4.10): no scripts, positions in
 * percent of the card, interactive elements and live data as `data-vp-*` attributes, effects as the
 * platform's classes. What the HTML uses goes to `uses`.
 */

export type CardEffect = 'none' | 'appear' | 'pulse' | 'sway' | 'spin' | 'shimmer';
export type CardTrigger = 'always' | 'hover' | 'press' | 'change';
export type CardElementKind = 'rect' | 'circle' | 'line' | 'text' | 'icon' | 'image' | 'button' | 'toggle' | 'checkbox' | 'counter' | 'note' | 'live';
export type CardLive = 'time' | 'date' | 'weekday' | 'countdown' | 'profile:canvases' | 'profile:objects' | 'profile:opens' | 'profile:days';

export interface CardElement {
  id: string;
  kind: CardElementKind;
  x: number;
  y: number;
  w: number;
  h: number;
  rot?: number;
  opacity?: number;
  /** Text and line colour. */
  color?: string;
  /** Fill. */
  fill?: string;
  radius?: number;
  text?: string;
  fontSize?: number;
  bold?: boolean;
  align?: 'left' | 'center' | 'right';
  effect?: CardEffect;
  trigger?: CardTrigger;
  /** The state key of an interactive element. */
  key?: string;
  /** A button: `canvas:<id>`, `https://…`; empty — an action the module hears. */
  link?: string;
  min?: number;
  max?: number;
  step?: number;
  live?: CardLive;
  /** Countdown target (ISO date). */
  date?: string;
  /** A picture, `data:image/…`. */
  src?: string;
}

export interface CardModel {
  title: string;
  w: number;
  h: number;
  look: CardLook;
  elements: CardElement[];
}

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const color = (c: string | undefined, d: string) => (c && /^#[0-9a-f]{3,8}$/i.test(c) ? c : d);
const num = (n: unknown, d: number) => (typeof n === 'number' && Number.isFinite(n) ? n : d);
const key = (k: string | undefined, id: string) => ((k ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 32) || 'k' + id.toLowerCase().replace(/[^a-z0-9]/g, '').slice(-8));

/** The card's own styles: switches, checkboxes, counters, notes. */
const CSS = `
.c{position:absolute;inset:0;overflow:hidden}
.e{position:absolute;box-sizing:border-box;display:flex;align-items:center;min-width:0;min-height:0}
.tx{white-space:pre-wrap;overflow:hidden;line-height:1.2}
.bt{justify-content:center;border-radius:10px;font-weight:600}
.tg{border-radius:999px;background:rgba(127,127,127,.35)}
.tg i{position:absolute;top:10%;left:6%;width:38%;height:80%;border-radius:999px;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.3)}
.tg.vp-on{background:var(--on,#22c55e)}
.tg.vp-on i{left:56%}
.ck{gap:8px}
.ck .bx{flex:none;width:1.2em;height:1.2em;border-radius:5px;border:2px solid currentColor;display:flex;align-items:center;justify-content:center;font-size:.85em}
.ck.vp-on .bx::after{content:"✓"}
.ck.vp-on .lb{text-decoration:line-through;opacity:.6}
.ct{justify-content:space-between;gap:6px}
.ct button{width:1.8em;height:1.8em;border:none;border-radius:50%;background:rgba(127,127,127,.25);color:inherit;font:inherit;font-weight:700;cursor:pointer}
.ct b{flex:1;text-align:center;font-variant-numeric:tabular-nums}
.nt{resize:none;border:1px solid rgba(127,127,127,.35);border-radius:8px;background:rgba(255,255,255,.08);padding:6px;outline:none}
`.trim();

/** HTML of the card and what it uses. */
export function generateCard(m: CardModel): { html: string; uses: CardUse[] } {
  const uses = new Set<CardUse>();
  const W = Math.max(1, m.w);
  const H = Math.max(1, m.h);
  const pct = (v: number, of: number) => Math.round((v / of) * 10000) / 100 + '%';
  const parts: string[] = [];
  for (const e of m.elements) {
    const style = [
      `left:${pct(e.x, W)}`,
      `top:${pct(e.y, H)}`,
      `width:${pct(e.w, W)}`,
      `height:${pct(e.h, H)}`,
      e.rot ? `transform:rotate(${num(e.rot, 0)}deg)` : '',
      e.opacity !== undefined && e.opacity < 1 ? `opacity:${num(e.opacity, 1)}` : '',
      `color:${color(e.color, '#1f2937')}`,
      e.fontSize ? `font-size:${num(e.fontSize, 14)}px` : '',
      e.bold ? 'font-weight:700' : '',
      e.align ? `justify-content:${e.align === 'left' ? 'flex-start' : e.align === 'right' ? 'flex-end' : 'center'};text-align:${e.align}` : '',
    ];
    const classes = ['e'];
    if (e.effect && e.effect !== 'none') {
      const t = e.trigger ?? 'always';
      classes.push(t === 'always' ? `vp-anim-${e.effect}` : `vp-on-${t}:${e.effect}`);
      uses.add(`anim:${e.effect}` as CardUse);
    }
    const fill = (d: string) => `background:${color(e.fill, d)}`;
    const k = key(e.key, e.id);
    let inner = '';
    let attrs = '';
    switch (e.kind) {
      case 'rect':
        style.push(fill('#e2e8f0'), `border-radius:${num(e.radius, 8)}px`);
        break;
      case 'circle':
        style.push(fill('#e2e8f0'), 'border-radius:50%');
        break;
      case 'line':
        style.push(`background:${color(e.color, '#94a3b8')}`, 'border-radius:999px');
        break;
      case 'text':
        classes.push('tx');
        inner = esc(e.text ?? '');
        break;
      case 'icon':
        classes.push('tx');
        style.push('justify-content:center');
        inner = esc(e.text ?? '⭐');
        break;
      case 'image':
        if (e.src && /^data:image\/(png|jpeg|webp|gif);base64,/.test(e.src)) {
          inner = `<img src="${e.src}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:${num(e.radius, 0)}px">`;
          uses.add('image');
        }
        break;
      case 'button':
        classes.push('bt');
        style.push(fill('#5e9cff'));
        inner = esc(e.text ?? '');
        if (e.link && /^(canvas:|bookmark:|https:\/\/)/.test(e.link)) {
          attrs = ` data-vp-link="${esc(e.link)}"`;
          uses.add('link');
        } else {
          attrs = ` data-vp-action="${k}"`;
          uses.add('action');
        }
        break;
      case 'toggle':
        classes.push('tg');
        style.push(`--on:${color(e.fill, '#22c55e')}`);
        inner = '<i></i>';
        attrs = ` data-vp-toggle="${k}" role="switch"`;
        uses.add('toggle');
        break;
      case 'checkbox':
        classes.push('ck');
        inner = `<span class="bx"></span><span class="lb">${esc(e.text ?? '')}</span>`;
        attrs = ` data-vp-toggle="${k}" role="checkbox"`;
        uses.add('toggle');
        break;
      case 'counter':
        classes.push('ct');
        inner = '<button data-vp-dec>−</button><b data-vp-value></b><button data-vp-inc>+</button>';
        attrs = ` data-vp-counter="${k}" data-vp-min="${num(e.min, 0)}" data-vp-max="${num(e.max, 99)}" data-vp-step="${num(e.step, 1)}"`;
        uses.add('counter');
        break;
      case 'note':
        inner = `<textarea class="nt" data-vp-note="${k}" placeholder="${esc(e.text ?? '')}" style="width:100%;height:100%;color:inherit;font-size:inherit"></textarea>`;
        uses.add('note');
        break;
      case 'live': {
        classes.push('tx');
        const live = e.live ?? 'time';
        const spec = live === 'countdown' ? `countdown:${esc(e.date ?? '')}` : live;
        attrs = ` data-vp-live="${spec}"`;
        uses.add(live.startsWith('profile:') ? 'live:profile' : live === 'countdown' ? 'live:countdown' : 'live:time');
        break;
      }
    }
    parts.push(`<div class="${classes.join(' ')}"${attrs} style="${style.filter(Boolean).join(';')}">${inner}</div>`);
  }
  if (m.look.background?.kind === 'image') uses.add('look:image');
  if (m.look.motion && m.look.motion !== 'none') uses.add('look:motion');
  const html = `<!doctype html><html><head><meta charset="utf-8"><style>${CSS}</style></head><body><div class="c">${parts.join('')}</div></body></html>`;
  return { html, uses: [...uses] };
}

let seq = 0;
export const newCardElementId = () => 'e' + Date.now().toString(36) + (seq++).toString(36);

/** Templates the editor offers. Words come from the module's locales. */
export function cardTemplates(t: (k: string) => string): { id: string; name: string; model: CardModel }[] {
  const base = (title: string, elements: Omit<CardElement, 'id'>[], look: CardLook): CardModel => ({ title, w: 300, h: 200, look, elements: elements.map((e) => ({ ...e, id: newCardElementId() })) });
  return [
    {
      id: 'habits',
      name: t('tpl.habits'),
      model: base(
        t('tpl.habits'),
        [
          { kind: 'text', x: 16, y: 12, w: 268, h: 30, text: t('tpl.habits'), fontSize: 18, bold: true, color: '#0f172a' },
          { kind: 'checkbox', x: 16, y: 54, w: 268, h: 30, text: t('tpl.habit1'), key: 'h1', fontSize: 15, color: '#0f172a', effect: 'pulse', trigger: 'change' },
          { kind: 'checkbox', x: 16, y: 92, w: 268, h: 30, text: t('tpl.habit2'), key: 'h2', fontSize: 15, color: '#0f172a', effect: 'pulse', trigger: 'change' },
          { kind: 'checkbox', x: 16, y: 130, w: 268, h: 30, text: t('tpl.habit3'), key: 'h3', fontSize: 15, color: '#0f172a', effect: 'pulse', trigger: 'change' },
        ],
        { background: { kind: 'gradient', type: 'linear', angle: 135, stops: [{ color: '#fef3c7', at: 0 }, { color: '#fde68a', at: 1 }] }, radius: 18 },
      ),
    },
    {
      id: 'water',
      name: t('tpl.water'),
      model: base(
        t('tpl.water'),
        [
          { kind: 'icon', x: 16, y: 20, w: 60, h: 60, text: '💧', fontSize: 40, effect: 'sway', trigger: 'always' },
          { kind: 'text', x: 88, y: 26, w: 196, h: 48, text: t('tpl.waterText'), fontSize: 16, bold: true, color: '#0c4a6e' },
          { kind: 'counter', x: 40, y: 110, w: 220, h: 56, key: 'glasses', min: 0, max: 12, step: 1, fontSize: 26, color: '#0c4a6e', effect: 'pulse', trigger: 'change' },
        ],
        { background: { kind: 'gradient', type: 'linear', angle: 160, stops: [{ color: '#e0f2fe', at: 0 }, { color: '#7dd3fc', at: 1 }] }, motion: 'waves', radius: 18 },
      ),
    },
    {
      id: 'countdown',
      name: t('tpl.countdown'),
      model: base(
        t('tpl.countdown'),
        [
          { kind: 'text', x: 16, y: 20, w: 268, h: 30, text: t('tpl.countdownText'), fontSize: 15, color: '#e0e7ff', align: 'center' },
          { kind: 'live', x: 16, y: 64, w: 268, h: 70, live: 'countdown', date: `${new Date().getFullYear() + 1}-01-01T00:00:00`, fontSize: 38, bold: true, color: '#ffffff', align: 'center', effect: 'appear', trigger: 'always' },
          { kind: 'live', x: 16, y: 148, w: 268, h: 30, live: 'date', fontSize: 13, color: '#c7d2fe', align: 'center' },
        ],
        { background: { kind: 'gradient', type: 'linear', angle: 135, stops: [{ color: '#312e81', at: 0 }, { color: '#7c3aed', at: 1 }] }, motion: 'particles', radius: 18 },
      ),
    },
    {
      id: 'note',
      name: t('tpl.note'),
      model: base(
        t('tpl.note'),
        [
          { kind: 'text', x: 16, y: 12, w: 268, h: 28, text: t('tpl.note'), fontSize: 16, bold: true, color: '#3f3f46' },
          { kind: 'note', x: 16, y: 48, w: 268, h: 136, key: 'text', text: t('tpl.notePlaceholder'), fontSize: 14, color: '#27272a' },
        ],
        { background: { kind: 'color', color: '#fef9c3' }, radius: 14, shadow: 'soft' },
      ),
    },
  ];
}
